export default function RogersHelpfulResourcesSection() {
  const vitalRecordsLinks = [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from the Arkansas Department of Health (ADH) Vital Records. Online, mail, or in-person options available.",
      url: "https://www.healthy.arkansas.gov/programs-services/program/certificates-and-records",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through ADH Vital Records. Required for estate and legal matters.",
      url: "https://www.healthy.arkansas.gov/programs-services/program/certificates-and-records",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request marriage license records from the Benton County Clerk. Certified copies available for name changes and legal purposes.",
      url: "https://bentoncountyar.gov/county-clerk/",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the Benton County Circuit Clerk. Required for name change passport corrections and legal documentation.",
      url: "https://bentoncountyar.gov/county-clerk/",
    },
  ];

  return (
    <section id="resources" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Links for Rogers Travelers</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { title: "U.S. State Department", desc: "Official passport requirements and forms.", link: "https://travel.state.gov/content/travel/en/passports.html" },
            { title: "TSA PreCheck", desc: "Speed through security at XNA, DFW, and ORD airports.", link: "https://www.tsa.gov/precheck" },
            { title: "Global Entry", desc: "Expedited customs clearance — ideal for Walmart frequent flyers.", link: "https://www.cbp.gov/travel/trusted-traveler-programs/global-entry" },
            { title: "CDC Travel Health", desc: "Vaccination and health notices for international destinations.", link: "https://wwwnc.cdc.gov/travel" },
            { title: "Smart Traveler Enrollment", desc: "Register your trip with the U.S. Embassy.", link: "https://step.state.gov/" },
            { title: "XNA Airport", desc: "NW Arkansas National Airport — direct flights to major hubs.", link: "https://www.flyxna.com" },
          ].map((r) => (
            <a key={r.title} href={r.link} target="_blank" rel="noopener noreferrer" className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all group">
              <h3 className="font-bold text-[#1A365D] text-lg mb-2 group-hover:text-[#F6C344] transition-colors">{r.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{r.desc}</p>
              <span className="inline-flex items-center gap-1 text-[#1A365D] text-sm font-semibold group-hover:text-[#F6C344] transition-colors">Visit <i className="ri-arrow-right-line"></i></span>
            </a>
          ))}
        </div>

        {/* Vital Records */}
        <div className="mt-10 bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
              <i className="ri-file-text-line text-white text-lg"></i>
            </div>
            <div>
              <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
              <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — Benton County</p>
            </div>
          </div>
          <div className="px-6 py-6">
            <p className="text-gray-500 text-sm leading-relaxed mb-5">
              If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records serving Rogers and Benton County residents. Most records can be ordered online for delivery within 5–10 business days.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              {vitalRecordsLinks.map((record, i) => (
                <a
                  key={i}
                  href={record.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                    <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                    <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                      <i className="ri-external-link-line text-xs"></i>Get Records
                    </span>
                  </div>
                </a>
              ))}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-gray-600 text-xs leading-relaxed">
                <i className="ri-information-line text-amber-500 mr-1.5"></i>
                <strong>Need records from another Arkansas county?</strong> Visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}